import {inject, Injectable} from "@angular/core";
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from "@angular/common/http";
import {ActivatedRouteSnapshot, Router} from "@angular/router";
import {environment} from "../../environments/environment";
import {from, lastValueFrom, Observable} from "rxjs";
import {jwtDecode} from "jwt-decode";
import {PatientJwt} from "dci-service-shared";

/** Handle authorization and authentication with the backend */
@Injectable({
	providedIn: 'root'
})
export class SessionService implements HttpInterceptor {
	/** Unauthenticated urls the user is allowed to interact with in the api */
	private static readonly UnauthenticatedUrls = new Set([
		`${environment.apiUrl}/dci/login`
	]);

	/** Current JWT being used */
	private currentToken:string;

	/** Current decoded JWT */
	private currentSession:PatientJwt;

	constructor(private router:Router) {
	}

	/** Can a logged in page be activated or do they need to log in? */
	public static canActivateAuthenticated(route:ActivatedRouteSnapshot) {
		return inject(SessionService).canActivate(route);
	}

	/** Is the user logged in? Or has a stored session */
	private canActivate(route:ActivatedRouteSnapshot) {
		this.loadSession();

		const surveyId = route.paramMap.get("surveyId");

		if(this.currentSession && !this.isExpired && this.currentSession.surveyLinkId == surveyId) {
			return true;
		}

		this.logout();

		//return true if the user has a session, otherwise send them to the login page
		return this.router.createUrlTree([surveyId, 'auth']);
	}

	/** Load the session and token from the {@link localStorage} */
	private loadSession() {
		if(!this.currentSession) {
			try {
				this.currentToken = localStorage.getItem("allai-session");
				if(this.currentToken) {
					this.currentSession = SessionService.decodeJwt(this.currentToken);
				}
			} catch(e) {
				console.warn("Failed to deserialize session");
				this.logout();
			}
		}
	}

	/**
	 * Decode JWT, this is mostly here because Jasmine can mock jwtDecode by itself, eventually when vitest comes out we
	 * can replace this
	 * @param token the token to decode
	 */
	static decodeJwt(token:string) {
		return jwtDecode<PatientJwt>(token);
	}

	/** Logout the patient */
	logout() {
		localStorage.removeItem("allai-session");
		this.currentSession = undefined;
		this.currentToken = undefined;
	}

	/** Intercept all REST calls and make sure a bearer is added if it is to the backend API */
	intercept(request:HttpRequest<any>, next:HttpHandler):Observable<HttpEvent<any>> {
		//if an api request
		if(!SessionService.UnauthenticatedUrls.has(request.url) && request.url.startsWith(environment.apiUrl)) {
			return from(this.handleApiRequest(request, next));
		}

		return next.handle(request);
	}

	/** Handle an API request and make sure we have a fresh JWT */
	private async handleApiRequest(request:HttpRequest<any>, next:HttpHandler) {
		const authorizedRequest:HttpRequest<any> = request.clone({
			setHeaders: {
				Authorization: 'Bearer ' + this.currentToken
			}
		});

		return lastValueFrom(next.handle(authorizedRequest));
	}

	/** Set a stored session */
	setSession(token:string) {
		this.currentToken = token;
		this.currentSession = jwtDecode<PatientJwt>(token);

		localStorage.setItem("allai-session", token);
	}

	/** Is the current session expired? */
	get isExpired() {
		return this.currentSession ? this.currentSession.exp * 1000 < new Date().getTime() : true;
	}

	/** Get a stored session from local storage */
	get session():PatientJwt {
		return this.currentSession;
	}
}
